// src/components/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from "./ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import TermsModal from './TermsModal';
import PrivacyPolicyModal from './PrivacyPolicyModal';

// Updated Logo Source
const LOGO_SRC = '/logo.png'; 
const API_ENDPOINT = 'https://perkexchange-production.up.railway.app/api/perks';

// Component for the Checkmark Icon (used in the Trust Bar)
const CheckIcon = (props) => (
  <svg 
    className="w-5 h-5 text-green-600" 
    fill="currentColor" 
    viewBox="0 0 20 20" 
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const StarIcon = () => (
    <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.049 2.927c.3-.921 1.691-.921 1.99 0l1.24 3.829a1 1 0 00.95.695h4.025c.969 0 1.371 1.24.588 1.81l-3.266 2.373a1 1 0 00-.362 1.118l1.24 3.829c.3.921-.755 1.688-1.54 1.118l-3.266-2.373a1 1 0 00-1.176 0l-3.266 2.373c-.784.57-1.84-.197-1.54-1.118l1.24-3.829a1 1 0 00-.362-1.118L2.004 9.261c-.783-.57-.381-1.81.588-1.81h4.025a1 1 0 00.95-.695l1.24-3.829z" />
    </svg>
);


const LandingPage = () => {
  const [perks, setPerks] = useState([]);
  const [featuredPerks, setFeaturedPerks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [openFAQ, setOpenFAQ] = useState(null);
  const carouselRef = useRef(null);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  useEffect(() => {
    const fetchPerks = async () => {
      try {
        // --- LIVE PRODUCTION DATA FETCH ---
        const response = await fetch(API_ENDPOINT);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        // Filter for public perks
        const publicPerks = data.filter(perk => 
          !perk.userId.discoverability || perk.userId.discoverability === 'public'
        );
        setPerks(publicPerks);
        
        // Determine featured perks (isHero property or highest value)
        const heroPerks = publicPerks.filter(perk => perk.isHero);
        if (heroPerks.length > 0) {
          setFeaturedPerks(heroPerks.slice(0, 6));
        } else {
          const sortedPerks = [...publicPerks].sort((a, b) => b.estimatedValue - a.estimatedValue);
          setFeaturedPerks(sortedPerks.slice(0, 6));
        }
      } catch (error) {
        console.error('Error fetching perks:', error);
        // Fallback or error state handling (keeping this for robustness)
        setPerks([]);
        setFeaturedPerks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPerks();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (featuredPerks.length <= 1) return;
    
    const interval = setInterval(() => {
      setActiveIndex((current) => 
        current === featuredPerks.length - 1 ? 0 : current + 1
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, [featuredPerks.length]);

  const nextSlide = () => {
    if (featuredPerks.length <= 1) return;
    setActiveIndex((current) => 
      current === featuredPerks.length - 1 ? 0 : current + 1
    );
  };

  const prevSlide = () => {
    if (featuredPerks.length <= 1) return;
    setActiveIndex((current) => 
      current === 0 ? featuredPerks.length - 1 : current - 1
    ); 
  };

  const goToSlide = (index) => {
    setActiveIndex(index);
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const steps = [
    { 
      title: "1. List Your Perk",
      description: "Post your unused corporate benefits quickly and easily. Get a quick valuation.",
      icon: (
        <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      )
    },
    { 
      title: "2. Secure Exchange",
      description: "Accept the best trade offer. We hold funds in escrow until both parties confirm the swap.",
      icon: (
        <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )
    },
    { 
      title: "3. Enjoy Your Reward",
      description: "Get the benefit you actually want! From gym passes to meal credits, the choice is yours.",
      icon: (
        <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c1.657 0 3 .895 3 2s-1.343 2-3 2-3 .895-3 2 1.343 2 3 2m0-8V6m0 10v2" />
        </svg>
      )
    }
  ];

  const faqItems = [
    {
      question: "Is this legal?",
      answer: "Yes, entirely. We facilitate the trade of unused benefits you already own. We are a peer-to-peer marketplace, not involved in payroll or HR systems. We encourage checking your company's policy, but most non-health benefits are fair game."
    },
    {
      question: "How do you ensure security and prevent scams?",
      answer: "We use escrow-style transactions: funds are held securely by us until both parties confirm the successful exchange of the perk. We also verify users via corporate email and maintain a robust rating system and dispute resolution process."
    },
    {
      question: "What types of perks can I trade?",
      answer: "Anything transferable and non-essential: gym memberships, meal/commute credits, subscriptions (Headspace, Spotify), event tickets, and office stipends. We do not handle health insurance or legally non-transferable benefits."
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ cursor: 'wait' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-t-4 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" style={{ cursor: 'default' }}>
      
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              {/* Logo in Nav Bar */}
              <img src={LOGO_SRC} alt="Perk Exchange Logo" className="h-9 w-auto" />
            </Link>
            
            {!isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm sm:text-base text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
                >
                  Log In
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  Sign Up
                </Link>
              </div>
            ) : (
              <Link
                to="/marketplace"
                className="px-4 py-2 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Go to Marketplace
              </Link>
            )}
          </div>
        </div>
      </nav>
      
      {/* Hero Section */}
      <header className="py-20 sm:py-32 bg-gray-50/50 flex flex-col items-center px-4">
        <div className="text-center max-w-5xl mx-auto">
            {/* Hero Logo (Slightly Reduced Size) */}
            <img 
                src={LOGO_SRC} 
                alt="Perk Exchange Logo Large" 
                className="h-14 sm:h-16 w-auto mx-auto mb-8 opacity-90" 
                style={{ filter: 'brightness(0.7)' }} // Added subtle filter to make it look less dominant than the text
            />
            
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
            Trade Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Unused Perks</span> for Value You Choose
          </h1>
          {/* Sub-headline size increased for better impact */}
          <p className="text-xl sm:text-3xl text-gray-600 mb-10 font-light max-w-3xl mx-auto">
            Securely swap meal credits, gym passes, and subscriptions for cash or other desirable benefits.
          </p>
          <Link
            to={isAuthenticated ? "/marketplace" : "/register"}
            className="inline-flex justify-center items-center px-10 sm:px-16 py-4 sm:py-5 text-lg sm:text-xl font-bold text-white bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-full transform transition-all hover:scale-[1.03] hover:shadow-2xl shadow-indigo-500/50"
          >
            {isAuthenticated ? "Browse Perks Now" : "Start Trading Today"}
          </Link>
          
          {/* Trust Bar (Added vertical margin) */}
          <div className="mt-12 flex justify-center items-center flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 font-medium">
            <span className="flex items-center space-x-1">
              <CheckIcon /> <span>Verified Company Emails</span>
            </span>
            <span className="flex items-center space-x-1">
              <CheckIcon /> <span>Escrow-Style Transactions</span>
            </span>
            <span className="flex items-center space-x-1">
              <CheckIcon /> <span>Dispute Resolution</span>
            </span>
          </div>
        </div>
      </header>
      
      {/* Featured Perks Showcase (Carousel) */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-12 sm:mb-16 text-gray-900">Featured Trades of the Week</h2>
          
          <div className="relative overflow-hidden rounded-2xl shadow-xl border border-gray-200">
            {featuredPerks.length > 0 ? (
              <>
                <div 
                  className="flex transition-transform duration-700 ease-in-out" 
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                  {featuredPerks.map((perk, index) => (
                    <div key={perk._id} className="w-full flex-shrink-0 relative">
                      <div className="h-80 sm:h-[400px] relative overflow-hidden">
                        {/* Image Handling */}
                        {perk.imageUrl && (
                          <img 
                            src={perk.imageUrl} 
                            alt={perk.title}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        )}
                        {/* Dark Gradient Overlay for text readability */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                        
                        <div className="relative z-20 h-full flex flex-col justify-end text-white p-6 sm:p-12">
                          <div className="max-w-xl">
                            <div className="inline-flex items-center px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold mb-4 border border-white/50">
                              <StarIcon /> <span className="ml-2">{perk.userId && perk.userId.averageRating ? perk.userId.averageRating.toFixed(1) : '5.0'} Trader Rating</span>
                            </div>
                            <h3 className="text-3xl sm:text-4xl font-bold mb-3">{perk.title}</h3>
                            <p className="text-lg sm:text-xl mb-6 opacity-90 line-clamp-2">{perk.description}</p>
                            <div className="flex flex-wrap items-center gap-4">
                              <div className="text-3xl sm:text-4xl font-extrabold text-white">
                                Value: ${perk.estimatedValue}
                              </div>
                              <div className="flex-1 hidden sm:block"></div>
                              <Link
                                to={isAuthenticated ? `/perk/${perk._id}` : "/register"}
                                className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 font-bold text-indigo-700 bg-white rounded-lg transform transition-all hover:scale-[1.05] hover:shadow-xl"
                              >
                                {isAuthenticated ? "View Trade" : "Sign Up to Trade"}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Carousel Controls */}
                <button 
                  onClick={prevSlide}
                  className="absolute top-1/2 left-4 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white rounded-full p-3 z-30 transition-all shadow-md"
                  aria-label="Previous slide"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button 
                  onClick={nextSlide}
                  className="absolute top-1/2 right-4 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white rounded-full p-3 z-30 transition-all shadow-md"
                  aria-label="Next slide"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
                
                {/* Dots */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-30">
                  {featuredPerks.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        index === activeIndex 
                          ? 'bg-white w-7' 
                          : 'bg-white/50 hover:bg-white/80'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-80 bg-gradient-to-br from-indigo-500 to-indigo-700 text-white rounded-2xl">
                <div className="text-center p-8">
                  <h3 className="text-2xl font-semibold mb-3">No Featured Perks Yet</h3>
                  <p className="text-lg opacity-80">Be the first to list a high-value perk and be featured here!</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      
      <hr className="my-0 border-gray-100" />
      
      {/* How It Works Section */}
      <section className="py-16 sm:py-24 max-w-7xl mx-auto px-4">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-12 sm:mb-16 text-gray-900">How It Works: Secure, Three-Step Swap</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-12">
          {steps.map((step, index) => (
            <Card key={index} className="flex flex-col items-center text-center p-6 border-2 border-indigo-100/50 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-6 border border-indigo-200">
                <div className="text-indigo-600">
                  {step.icon}
                </div>
              </div>
              <CardTitle className="text-xl font-bold text-gray-900 mb-3">{step.title}</CardTitle>
              <CardDescription className="text-base text-gray-600">{step.description}</CardDescription>
            </Card>
          ))}
        </div>
      </section>
      
      <hr className="my-0 border-gray-100" />

      {/* Available Perks Preview (Grid) */}
      <section className="py-16 sm:py-24 max-w-7xl mx-auto px-4">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-12 sm:mb-16 text-gray-900">Explore All Current Market Listings</h2>
        
        {perks.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {perks.slice(0, 6).map((perk) => (
                <Card key={perk._id} className="overflow-hidden border-2 border-gray-100 hover:shadow-xl hover:border-indigo-300/50 transition-all duration-300">
                {perk.imageUrl && (
                    <div className="h-56 overflow-hidden">
                    <img 
                        src={perk.imageUrl} 
                        alt={perk.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                    </div>
                )}
                <CardHeader className="p-5 pb-3">
                    <div className="flex items-start justify-between">
                    <CardTitle className="text-xl font-bold text-gray-900 line-clamp-1">
                        {perk.title}
                    </CardTitle>
                    <div className="text-2xl font-extrabold text-indigo-600 ml-3 shrink-0">
                        ${perk.estimatedValue}
                    </div>
                    </div>
                </CardHeader>
                <CardContent className="px-5 pt-0">
                    <CardDescription className="text-gray-600 line-clamp-2 text-base mb-3">
                    {perk.description}
                    </CardDescription>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                    <TooltipProvider>
                        <Tooltip>
                        <TooltipTrigger>
                            <div className="flex items-center font-medium">
                            <StarIcon /> <span className="ml-1">{perk.userId && perk.userId.averageRating ? perk.userId.averageRating.toFixed(1) : '5.0'}</span>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent><p>User Rating</p></TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <span className="text-gray-300">•</span>
                    <span className="font-medium text-indigo-500">{perk.userId?.company || 'Verified Company'}</span>
                    </div>
                </CardContent>
                <CardFooter className="p-5 pt-0">
                    {!isAuthenticated ? (
                    <Link
                        to="/register"
                        className="w-full bg-gradient-to-r from-indigo-600 to-indigo-800 text-white py-3 px-4 rounded-lg text-center font-bold hover:shadow-lg transition-all hover:brightness-110"
                    >
                        Sign Up to Trade
                    </Link>
                    ) : (
                    <Link
                        to="/marketplace"
                        className="w-full bg-indigo-50 border border-indigo-200 text-indigo-700 py-3 px-4 rounded-lg text-center font-bold hover:bg-indigo-100 transition-colors"
                    >
                        View in Marketplace
                    </Link>
                    )}
                </CardFooter>
                </Card>
            ))}
            </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl shadow-md border-2 border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">The Marketplace is Growing!</h3>
            <p className="text-lg text-gray-600">No public perks available right now. Be the first to list yours!</p>
          </div>
        )}
        
        
        <div className="text-center mt-12">
          <Link
            to={isAuthenticated ? "/marketplace" : "/register"}
            className="inline-flex justify-center items-center px-12 py-4 text-lg font-bold text-indigo-700 bg-indigo-100 border-2 border-indigo-300 rounded-full hover:bg-indigo-200 transition-colors shadow-md"
          >
            {isAuthenticated ? "View All Perks" : "Join Perk Exchange Today"}
          </Link>
        </div>
      </section>

      {/* FAQ Section (Moved and Shortened) */}
      <section className="py-16 sm:py-24 bg-gray-50/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Fast answers to your most pressing questions about trading perks.</p>
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-indigo-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{item.question}</h3>
                  <svg
                    className={`w-5 h-5 text-indigo-600 transform transition-transform ${
                      openFAQ === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6 pt-0">
                    <p className="text-gray-700 leading-relaxed text-base border-t border-gray-100 pt-4">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <span className="text-center sm:text-left">© 2025 Perk Exchange. All rights reserved.</span>
            <div className="flex space-x-4">
              <button 
                onClick={() => setIsTermsModalOpen(true)}
                className="hover:text-indigo-600 transition-colors font-medium"
              >
                Terms & Conditions
              </button>
              <span className="text-gray-300">|</span>
              <button 
                onClick={() => setIsPrivacyModalOpen(true)}
                className="hover:text-indigo-600 transition-colors font-medium"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Modals */}
      <TermsModal 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
        onAccept={() => setIsTermsModalOpen(false)}
        fullName="User"
      />
      
      <PrivacyPolicyModal 
        isOpen={isPrivacyModalOpen} 
        onClose={() => setIsPrivacyModalOpen(false)} 
      />
    </div>
  );
};

export default LandingPage;